'use client'
import { useEffect, useRef } from 'react'

// Continuous chalk-drawing animation — like a coach drawing X's and O's on a board.
// The pen never stops: one play fades while the next begins, always drawing.

export default function HeroBackgroundDynamic() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!

    const resize = () => {
      canvas.width  = canvas.parentElement?.offsetWidth  || window.innerWidth
      canvas.height = canvas.parentElement?.offsetHeight || 600
    }
    resize()
    window.addEventListener('resize', resize)

    // ── Point pre-computation helpers ─────────────────────────────────────────
    // All jitter is computed ONCE at build time and stored as point arrays.
    // The render loop replays stored points — no re-randomization per frame.

    function computeLine(
      x1: number, y1: number, x2: number, y2: number,
    ): [number, number][] {
      return [[x1, y1], [x2, y2]]
    }

    function computeCurve(
      x1: number, y1: number, cx: number, cy: number, x2: number, y2: number,
    ): [number, number][] {
      const steps = 36
      const pts: [number, number][] = [[x1, y1]]
      for (let i = 1; i <= steps; i++) {
        const t = i / steps
        pts.push([
          (1-t)*(1-t)*x1 + 2*(1-t)*t*cx + t*t*x2,
          (1-t)*(1-t)*y1 + 2*(1-t)*t*cy + t*t*y2,
        ])
      }
      return pts
    }

    // ── Render helpers ────────────────────────────────────────────────────────

    function renderPts(pts: [number, number][], progress: number, alpha: number, width: number, color: string) {
      const end = Math.max(2, Math.floor(pts.length * progress))
      ctx.beginPath()
      ctx.moveTo(pts[0][0], pts[0][1])
      for (let i = 1; i < end; i++) ctx.lineTo(pts[i][0], pts[i][1])
      ctx.strokeStyle = `rgba(${color},${alpha})`
      ctx.lineWidth   = width
      ctx.lineCap     = 'round'
      ctx.lineJoin    = 'round'
      ctx.stroke()
    }

    function renderCircle(x: number, y: number, r: number, alpha: number, color: string, progress: number) {
      ctx.beginPath()
      ctx.arc(x, y, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress)
      ctx.strokeStyle = `rgba(${color},${alpha})`
      ctx.lineWidth   = 1.8
      ctx.lineCap     = 'round'
      ctx.stroke()
    }

    function renderArrow(x: number, y: number, angle: number, alpha: number, color: string) {
      const size = 10
      ctx.save()
      ctx.translate(x, y)
      ctx.rotate(angle)
      ctx.beginPath()
      ctx.moveTo(0, 0)
      ctx.lineTo(-size, -size * 0.5)
      ctx.moveTo(0, 0)
      ctx.lineTo(-size, size * 0.5)
      ctx.strokeStyle = `rgba(${color},${alpha})`
      ctx.lineWidth   = 1.6
      ctx.lineCap     = 'round'
      ctx.stroke()
      ctx.restore()
    }

    // ── Stroke & play types ───────────────────────────────────────────────────

    type StrokeDef = {
      type   : 'line' | 'circle' | 'x' | 'curve' | 'arrow'
      startT : number
      endT   : number
      alpha  : number
      color  : string
      params : number[]
    }

    // A compiled stroke has pre-computed geometry ready to replay
    type CompiledStroke = StrokeDef & {
      pts  : [number, number][]   // pre-computed for line/curve/x
      pts2 : [number, number][]   // second arm for x
    }

    type CompiledPlay = CompiledStroke[]

    // ── Compile: compute all geometry upfront ─────────────────────────────────

    const compile = (defs: StrokeDef[]): CompiledPlay =>
      defs.map(d => {
        let pts:  [number, number][] = []
        let pts2: [number, number][] = []
        const p = d.params
        if (d.type === 'line') {
          pts = computeLine(p[0], p[1], p[2], p[3])
        } else if (d.type === 'curve') {
          pts = computeCurve(p[0], p[1], p[2], p[3], p[4], p[5])
        } else if (d.type === 'x') {
          const s = p[2] * 0.75
          pts  = computeLine(p[0]-s, p[1]-s, p[0]+s, p[1]+s)
          pts2 = computeLine(p[0]+s, p[1]-s, p[0]-s, p[1]+s)
        }
        return { ...d, pts, pts2 }
      })

    // ── Play definitions (same geometry as before) ────────────────────────────

    const buildPlays = (w: number, h: number): CompiledPlay[] => {
      const cx = w * 0.5, cy = h * 0.5
      const sc = Math.min(w, h) / 580

      const bball = (): StrokeDef[] => {
        const ox = cx - w * 0.04, oy = cy + h * 0.04, s = 58 * sc
        return [
          { type: 'line',   startT: 0,    endT: 0.08, alpha: 0.07, color: '255,255,255', params: [ox-s*.6, oy+s*1.8, ox+s*.6, oy+s*1.8] },
          { type: 'line',   startT: 0.04, endT: 0.10, alpha: 0.07, color: '255,255,255', params: [ox-s*.6, oy+s*1.8, ox-s*.6, oy-s*.5] },
          { type: 'line',   startT: 0.04, endT: 0.10, alpha: 0.07, color: '255,255,255', params: [ox+s*.6, oy+s*1.8, ox+s*.6, oy-s*.5] },
          { type: 'circle', startT: 0.10, endT: 0.20, alpha: 0.30, color: '232,212,77',  params: [ox-s*1.8, oy+s*.3, s*.33] },
          { type: 'circle', startT: 0.10, endT: 0.20, alpha: 0.24, color: '255,255,255', params: [ox+s*1.5, oy-s*.8, s*.28] },
          { type: 'circle', startT: 0.10, endT: 0.20, alpha: 0.22, color: '255,255,255', params: [ox-s*.4,  oy,      s*.28] },
          { type: 'circle', startT: 0.10, endT: 0.20, alpha: 0.20, color: '255,255,255', params: [ox+s*.2,  oy+s*1., s*.28] },
          { type: 'x',      startT: 0.20, endT: 0.28, alpha: 0.24, color: '232,212,77',  params: [ox-s*1.7, oy+s*.8, s*.24] },
          { type: 'x',      startT: 0.20, endT: 0.28, alpha: 0.20, color: '232,212,77',  params: [ox-s*.3,  oy+s*.1, s*.22] },
          { type: 'curve',  startT: 0.28, endT: 0.54, alpha: 0.32, color: '232,212,77',  params: [ox-s*1.8, oy+s*.3, ox-s*.9, oy-s*.5, ox-s*.4, oy, 0] },
          { type: 'line',   startT: 0.46, endT: 0.56, alpha: 0.27, color: '255,255,255', params: [ox-s*.6, oy-s*.4, ox-s*.1, oy-s*.4] },
          { type: 'curve',  startT: 0.56, endT: 0.78, alpha: 0.34, color: '232,212,77',  params: [ox-s*.4, oy, ox+s*.3, oy-s*.8, ox+s*.9, oy-s*.6, 1] },
          { type: 'line',   startT: 0.80, endT: 0.94, alpha: 0.30, color: '232,212,77',  params: [ox+s*1.5, oy-s*.8, ox+s*.9, oy-s*.6] },
          { type: 'arrow',  startT: 0.94, endT: 1.00, alpha: 0.30, color: '232,212,77',  params: [ox+s*.9, oy-s*.6, Math.atan2(-s*.2, -s*.6)] },
        ]
      }

      const soccer = (): StrokeDef[] => {
        const ox = cx + w*.10, oy = cy - h*.06, s = 62 * sc
        return [
          { type: 'line',   startT: 0,    endT: 0.07, alpha: 0.07, color: '255,255,255', params: [ox-s*1.4, oy-s*1.8, ox+s*1.4, oy-s*1.8] },
          { type: 'line',   startT: 0.02, endT: 0.08, alpha: 0.07, color: '255,255,255', params: [ox-s*1.4, oy-s*1.8, ox-s*1.4, oy-s*2.4] },
          { type: 'line',   startT: 0.02, endT: 0.08, alpha: 0.07, color: '255,255,255', params: [ox+s*1.4, oy-s*1.8, ox+s*1.4, oy-s*2.4] },
          { type: 'line',   startT: 0.04, endT: 0.10, alpha: 0.07, color: '255,255,255', params: [ox-s*1.4, oy-s*2.4, ox+s*1.4, oy-s*2.4] },
          { type: 'x',      startT: 0.10, endT: 0.20, alpha: 0.24, color: '232,212,77',  params: [ox-s*.55, oy-s*1.0, s*.22] },
          { type: 'x',      startT: 0.12, endT: 0.22, alpha: 0.24, color: '232,212,77',  params: [ox-s*.20, oy-s*1.0, s*.22] },
          { type: 'x',      startT: 0.14, endT: 0.24, alpha: 0.24, color: '232,212,77',  params: [ox+s*.15, oy-s*1.0, s*.22] },
          { type: 'x',      startT: 0.16, endT: 0.26, alpha: 0.24, color: '232,212,77',  params: [ox+s*.50, oy-s*1.0, s*.22] },
          { type: 'circle', startT: 0.22, endT: 0.32, alpha: 0.32, color: '232,212,77',  params: [ox-s*1.6, oy+s*.5, s*.30] },
          { type: 'circle', startT: 0.22, endT: 0.32, alpha: 0.22, color: '255,255,255', params: [ox,       oy+s*.8, s*.28] },
          { type: 'curve',  startT: 0.34, endT: 0.74, alpha: 0.40, color: '232,212,77',  params: [ox-s*1.6, oy+s*.5, ox+s*1.8, oy-s*.2, ox+s*1.1, oy-s*2.2, 1] },
          { type: 'curve',  startT: 0.42, endT: 0.76, alpha: 0.20, color: '255,255,255', params: [ox, oy+s*.8, ox+s*.3, oy-s*.3, ox-s*.5, oy-s*1.4, 0] },
        ]
      }

      const volleyball = (): StrokeDef[] => {
        const ox = cx - w*.08, oy = cy + h*.02, s = 54 * sc
        return [
          { type: 'line',   startT: 0,    endT: 0.10, alpha: 0.09, color: '255,255,255', params: [ox-s*2., oy-s*.8, ox+s*2., oy-s*.8] },
          { type: 'circle', startT: 0.10, endT: 0.20, alpha: 0.26, color: '255,255,255', params: [ox-s*1.2, oy+s*1.6, s*.28] },
          { type: 'circle', startT: 0.10, endT: 0.20, alpha: 0.26, color: '255,255,255', params: [ox,        oy+s*1.8, s*.28] },
          { type: 'circle', startT: 0.10, endT: 0.20, alpha: 0.26, color: '255,255,255', params: [ox+s*1.2,  oy+s*1.6, s*.28] },
          { type: 'circle', startT: 0.10, endT: 0.20, alpha: 0.32, color: '232,212,77',  params: [ox+s*.6,  oy+s*.2, s*.30] },
          { type: 'circle', startT: 0.10, endT: 0.20, alpha: 0.26, color: '255,255,255', params: [ox-s*1.5, oy-s*.1, s*.28] },
          { type: 'curve',  startT: 0.20, endT: 0.45, alpha: 0.24, color: '255,255,255', params: [ox+s*2.2, oy-s*2.5, ox+s*.2, oy-s*1.8, ox, oy+s*1.8, 0] },
          { type: 'curve',  startT: 0.45, endT: 0.63, alpha: 0.30, color: '232,212,77',  params: [ox, oy+s*1.8, ox+s*.4, oy+s*.8, ox+s*.6, oy+s*.2, 0] },
          { type: 'curve',  startT: 0.63, endT: 0.80, alpha: 0.34, color: '232,212,77',  params: [ox+s*.6, oy+s*.2, ox-s*.4, oy-s*.6, ox-s*1.5, oy-s*.1, 0] },
          { type: 'curve',  startT: 0.80, endT: 0.96, alpha: 0.42, color: '232,212,77',  params: [ox-s*1.5, oy-s*.1, ox-s*.8, oy-s*1.8, ox-s*.2, oy-s*2.8, 1] },
        ]
      }

      const baseball = (): StrokeDef[] => {
        const ox = cx + w*.05, oy = cy + h*.08, bs = 72 * sc
        return [
          { type: 'line',   startT: 0,    endT: 0.06, alpha: 0.07, color: '255,255,255', params: [ox, oy+bs, ox+bs, oy] },
          { type: 'line',   startT: 0.02, endT: 0.08, alpha: 0.07, color: '255,255,255', params: [ox+bs, oy, ox, oy-bs] },
          { type: 'line',   startT: 0.04, endT: 0.10, alpha: 0.07, color: '255,255,255', params: [ox, oy-bs, ox-bs, oy] },
          { type: 'line',   startT: 0.06, endT: 0.12, alpha: 0.07, color: '255,255,255', params: [ox-bs, oy, ox, oy+bs] },
          { type: 'circle', startT: 0.12, endT: 0.22, alpha: 0.30, color: '232,212,77',  params: [ox+bs, oy, bs*.18] },
          { type: 'circle', startT: 0.12, endT: 0.22, alpha: 0.28, color: '232,212,77',  params: [ox, oy+bs, bs*.18] },
          { type: 'x',      startT: 0.20, endT: 0.30, alpha: 0.22, color: '232,212,77',  params: [ox+bs*.35, oy-bs*.35, bs*.14] },
          { type: 'x',      startT: 0.20, endT: 0.30, alpha: 0.22, color: '232,212,77',  params: [ox-bs*.3,  oy-bs*.45, bs*.14] },
          { type: 'line',   startT: 0.30, endT: 0.44, alpha: 0.30, color: '232,212,77',  params: [ox, oy-bs*.12, ox, oy+bs*.85] },
          { type: 'arrow',  startT: 0.44, endT: 0.50, alpha: 0.28, color: '232,212,77',  params: [ox, oy+bs*.85, Math.PI/2] },
          { type: 'curve',  startT: 0.50, endT: 0.70, alpha: 0.32, color: '232,212,77',  params: [ox+bs, oy, ox+bs*.9, oy-bs*.5, ox, oy-bs, 1] },
          { type: 'curve',  startT: 0.54, endT: 0.80, alpha: 0.34, color: '232,212,77',  params: [ox, oy+bs*.85, ox+bs*1.4, oy-bs*.3, ox+bs*1.8, oy-bs*1.2, 1] },
          { type: 'curve',  startT: 0.76, endT: 0.98, alpha: 0.26, color: '255,255,255', params: [ox, oy+bs, ox+bs*.6, oy+bs*.5, ox+bs, oy, 0] },
        ]
      }

      return [bball(), soccer(), volleyball(), baseball()].map(compile)
    }

    // ── Continuous animation state ────────────────────────────────────────────

    const DRAW_FRAMES = 300
    const FADE_FRAMES = 90
    const OVERLAP     = 40

    let plays: CompiledPlay[]
    let frameCount = 0
    let raf: number

    let primary   = { index: 0, start: 0 }
    let secondary : { index: number; start: number; fadeStart: number } | null = null

    const rebuild = () => { plays = buildPlays(canvas.width, canvas.height) }
    rebuild()

    const drawPlay = (playIdx: number, startFrame: number, globalAlpha: number) => {
      if (!plays) return
      const progress = Math.min(1, (frameCount - startFrame) / DRAW_FRAMES)
      const strokes  = plays[playIdx] ?? []

      for (const stroke of strokes) {
        if (stroke.startT > progress) continue
        const sp = Math.min(1, (progress - stroke.startT) / Math.max(0.001, stroke.endT - stroke.startT))
        if (sp <= 0) continue

        const a = stroke.alpha * globalAlpha
        const p = stroke.params
        const c = stroke.color

        if (stroke.type === 'line') {
          if (stroke.pts.length >= 2) renderPts(stroke.pts, sp, a, 1.6, c)
        } else if (stroke.type === 'circle') {
          renderCircle(p[0], p[1], p[2], a, c, sp)
        } else if (stroke.type === 'x') {
          if (sp > 0.5) {
            if (stroke.pts.length  >= 2) renderPts(stroke.pts,  1, a, 1.4, c)
            if (stroke.pts2.length >= 2) renderPts(stroke.pts2, 1, a, 1.4, c)
          }
        } else if (stroke.type === 'arrow') {
          if (sp > 0.7) renderArrow(p[0], p[1], p[2], a, c)
        } else if (stroke.type === 'curve') {
          if (stroke.pts.length >= 2) {
            renderPts(stroke.pts, sp, a, 1.4, c)
            // arrowhead at end of curve
            if (p[6] === 1 && sp >= 0.96) {
              const pts = stroke.pts
              const last = pts[pts.length - 1]
              const prev = pts[Math.max(0, pts.length - 5)]
              renderArrow(last[0], last[1], Math.atan2(last[1] - prev[1], last[0] - prev[0]), a, c)
            }
          }
        }
      }
    }

    const draw = () => {
      raf = requestAnimationFrame(draw)
      frameCount++

      const primaryAge = frameCount - primary.start
      if (primaryAge >= DRAW_FRAMES - OVERLAP && secondary === null) {
        secondary = { ...primary, fadeStart: frameCount }
        primary   = { index: (primary.index + 1) % plays.length, start: frameCount }
        rebuild()
      }
      if (secondary !== null) {
        if (frameCount - secondary.fadeStart >= FADE_FRAMES) secondary = null
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      if (secondary !== null) {
        const fadeAlpha = Math.max(0, 1 - (frameCount - secondary.fadeStart) / FADE_FRAMES)
        drawPlay(secondary.index, secondary.start, fadeAlpha)
      }
      drawPlay(primary.index, primary.start, 1)

      // Soft chalk dust particles
      if (frameCount % 10 === 0) {
        const r = Math.random() * 1.0 + 0.3
        const a = Math.random() * 0.05 + 0.01
        ctx.save()
        ctx.shadowBlur  = 6
        ctx.shadowColor = `rgba(237,232,220,${a * 3})`
        ctx.globalAlpha = a
        ctx.fillStyle   = 'rgba(237,232,220,0.9)'
        ctx.beginPath()
        ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, r, 0, Math.PI * 2)
        ctx.fill()
        ctx.restore()
      }
    }

    draw()

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ willChange: 'transform' }}
      aria-hidden="true"
    />
  )
}
